import {
	Camera,
	CameraResultType,
	CameraSource,
	Photo,
} from "@capacitor/camera";
import {
	createContext,
	ReactElement,
	useContext,
	useMemo,
	useState,
} from "react";
import { from, map, Observable, switchMap } from "rxjs";
import { v4 } from "uuid";
import { UserPhoto } from "../hooks";
//@ts-ignore
import ImageBlobReduce from "image-blob-reduce";
//@ts-ignore
import Pica from "pica";
import { SearchResultItem } from "../api";
import { DEFAULT_PHOTO_QUALITY } from '../constants';

const pica = Pica({ features: ["js", "wasm", "cib"] });
const imageBlobReduce = new ImageBlobReduce({ pica });

interface SearchProviderProps {
	children?: ReactElement;
}

interface SearchContextInterface {
	currentImage?: UserPhoto;
	drawing?: number[][];
	isTextSearch: boolean;
	newSearch(): void;
	startSearch(drawing: number[][], isTextSearch: boolean): void;
	startTextSearch(searchTerm: string): void;
	setSearchTerm(searchTerm: string): void;
	searchId?: string;
	searchTerm?: string;
	searchResults: SearchResultItem[];
	setSearchResults(searchResults: SearchResultItem[]): void;
	takePhoto(): Observable<UserPhoto>;
	setCurrentImage(image: UserPhoto): void;
}

const SearchContext = createContext<SearchContextInterface>(
	{} as SearchContextInterface
);

export function SearchProvider({ children }: SearchProviderProps) {
	const [currentImage, setCurrentImage] = useState<UserPhoto | undefined>();
	const [drawing, setDrawing] = useState<number[][]>();
	const [isTextSearch, setIsTextSearch] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<string | undefined>();
	const [searchId, setSearchId] = useState<string | undefined>();
	const [searchResults, setSearchResults] = useState<SearchResultItem[]>([]);

	function startSearch(drawing: number[][], isTextSearch: boolean) {
		// setCurrentImage(image);
		setDrawing(drawing);
		setIsTextSearch(isTextSearch);
		setSearchId(v4());
		setSearchResults([]);
	}

	function startTextSearch(searchTerm: string) {
		setSearchId(v4());
		setSearchResults([]);
		setSearchTerm(searchTerm);
	}

	function newSearch() {
		setCurrentImage(undefined);
		setDrawing([]);
		setIsTextSearch(false);
		setSearchTerm(undefined);
		setSearchId(undefined);
		setSearchResults([]);
	}

	function takePhoto(): Observable<UserPhoto> {
		return from(
			Camera.getPhoto({
				resultType: CameraResultType.Uri,
				source: CameraSource.Camera,
				quality: DEFAULT_PHOTO_QUALITY,
			})
		).pipe(
			switchMap((cameraPhoto: Photo) => {
				return from(fetch(cameraPhoto.webPath!));
			}),
			switchMap((response: any) => {
				return from(response.blob());
			}),
			switchMap((blob) => {
				return from(imageBlobReduce.toBlob(blob, { max: 700 }));
			}),
			map((blob: any) => {
				const fileName = `search-photo-${new Date().getTime()}.jpeg`;
				const savedPhoto = {
					blobPath: window.URL.createObjectURL(blob),
					filepath: fileName,
				};
				setCurrentImage(savedPhoto);
				return savedPhoto;
			})
		);
	}

	const memo = useMemo(
		() => ({
			currentImage,
			drawing,
			isTextSearch,
			newSearch,
			searchId,
			searchTerm,
			searchResults,
			startSearch,
			startTextSearch,
			setSearchTerm,
			setSearchResults,
			takePhoto,
			setCurrentImage
		}),
		[
			currentImage,
			drawing,
			isTextSearch,
			searchId,
			searchTerm,
			searchResults,
			setCurrentImage
		]
	);

	return (
		<SearchContext.Provider value={memo}>{children}</SearchContext.Provider>
	);
}

export default function usePartSearch() {
	return useContext(SearchContext);
}
